/**
 * My Courses Component - Student Course Dashboard
 * Udemy-style course cards with progress tracking
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExitToApp as LogoutIcon, 
  Keyboard as KeyboardIcon,
  Terminal as TerminalIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Lock as LockIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Stack,
  Paper,
  Alert,
  CircularProgress,
  Avatar,
  FormControlLabel,
  Checkbox,
  Link,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCourseProgress } from '../../services/lmsProgressTracking';
import { formatCourseTitle, getCourseTheme } from './myCoursesTheme';
import { hasUnlimitedCourseAccess } from '../../utils/studentDevAccess';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();
const STUDENT_ANDROID_APK_URL = 'https://play.google.com/store/apps/details?id=com.knls.academy';

const normalizeCourseCode = (value) => String(value || '').trim().toLowerCase();

const buildCourseAccessMap = (courseAccessList = []) => {
  const map = new Map();
  (courseAccessList || []).forEach((item) => {
    map.set(normalizeCourseCode(item.course_code), item);
  });
  return map;
};

const resolveCourseExpiry = (student, courseCode, courseAccessMap) => {
  if (hasUnlimitedCourseAccess(student)) {
    return { isExpired: false, daysRemaining: null, expiryDate: null };
  }

  const access = courseAccessMap.get(normalizeCourseCode(courseCode));
  if (access) {
    const expiryDate = access.expires_at ? new Date(access.expires_at) : null;
    const daysRemaining = typeof access.days_remaining === 'number'
      ? access.days_remaining
      : expiryDate
        ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24))
        : null;
    return {
      isExpired: access.is_expired === true,
      daysRemaining,
      expiryDate,
    };
  }

  if (!student?.joining_date) {
    return { isExpired: false, daysRemaining: null, expiryDate: null };
  }

  const joiningDate = new Date(student.joining_date);
  const durationDays = student.duration_days || 45;
  const expiryDate = new Date(joiningDate);
  expiryDate.setDate(expiryDate.getDate() + durationDays);
  const daysRemaining = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
  return {
    isExpired: daysRemaining < 0,
    daysRemaining,
    expiryDate,
  };
};

const buildCourseMetaMap = (dashboardCourses = [], catalogCourses = []) => {
  const map = new Map();

  const upsert = (course) => {
    const code = course?.course_code || course?.courseCode;
    if (!code) return;
    const key = normalizeCourseCode(code);
    const existing = map.get(key) || {};
    map.set(key, {
      course_name: course.course_name || course.courseName || existing.course_name,
      description: course.description || existing.description || '',
      thumbnail_url: course.thumbnail_url || course.thumbnailUrl || existing.thumbnail_url || null,
      enrollment_count: course.enrollment_count ?? course.enrollmentCount ?? existing.enrollment_count ?? 0,
      show_enrollment_count: course.show_enrollment_count ?? course.showEnrollmentCount ?? existing.show_enrollment_count,
      show_practice_ide:
        course.show_practice_ide === true
        || course.showPracticeIde === true
        || existing.show_practice_ide === true,
    });
  };

  dashboardCourses.forEach(upsert);
  catalogCourses.forEach(upsert);
  return map;
};

const getCourseMeta = (courseMetaMap, courseCode) => {
  return courseMetaMap.get(normalizeCourseCode(courseCode)) || {};
};

const MyCourses = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState(null);
  const [coursesWithProgress, setCoursesWithProgress] = useState([]);
  const [demoCourses, setDemoCourses] = useState([]);
  const [enrollingCourseCode, setEnrollingCourseCode] = useState(null);
  const [courseAccessTermsOk, setCourseAccessTermsOk] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCourses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return coursesWithProgress;

    return coursesWithProgress.filter((course) => {
      const haystack = [
        course.courseName,
        course.courseCode,
        course.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [coursesWithProgress, searchQuery]);

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_data');
    toast.success('Logged out successfully');
    navigate('/student/login');
  };

  useEffect(() => {
    loadStudentAndCourses();
  }, []);

  useEffect(() => {
    if (studentData?.accepted_terms) {
      setCourseAccessTermsOk(true);
    }
  }, [studentData]);

  const loadStudentAndCourses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('student_token');
      const studentDataStr = localStorage.getItem('student_data');
      if (!token || !studentDataStr) {
        toast.error('Please login first');
        navigate('/student/login');
        return;
      }

      const cachedStudent = JSON.parse(studentDataStr);
      let student = cachedStudent;
      let availableDemoCourses = [];
      let dashboardCourses = [];
      let catalogCourses = [];

      // Set cached student first so UI doesn't collapse on transient API errors
      setStudentData(cachedStudent);

      try {
        const [dashboardResponse, catalogResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/students/my-dashboard`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${API_BASE_URL}/students/courses/list`),
        ]);

        if (dashboardResponse.ok) {
          const dashboardData = await dashboardResponse.json();
          student = dashboardData?.student || cachedStudent;
          availableDemoCourses = dashboardData?.student?.demo_courses || [];
          dashboardCourses = dashboardData?.student?.courses || [];
        } else {
          console.warn('My dashboard API failed with status:', dashboardResponse.status);
        }

        if (catalogResponse.ok) {
          const catalogData = await catalogResponse.json();
          catalogCourses = catalogData?.courses || [];
        }
      } catch (dashboardError) {
        console.warn('My dashboard API failed, using cached student data:', dashboardError?.message || dashboardError);
      }

      if (!Array.isArray(availableDemoCourses) || availableDemoCourses.length === 0) {
        try {
          const demoResponse = await fetch(`${API_BASE_URL}/students/courses/demo/public`);
          if (demoResponse.ok) {
            const demoData = await demoResponse.json();
            availableDemoCourses = demoData?.demo_courses || [];
          }
        } catch (demoError) {
          console.warn('Public demo courses API failed:', demoError?.message || demoError);
        }
      }
      const demoCourseNameMap = new Map(
        availableDemoCourses.map((course) => [course.demo_course_code, course.demo_course_name || course.demo_course_code]),
      );
      const demoCourseCodeSet = new Set(
        availableDemoCourses.map((course) => String(course.demo_course_code || '').toUpperCase()),
      );

      setDemoCourses(availableDemoCourses);
      setStudentData(student);
      localStorage.setItem('student_data', JSON.stringify(student));

      const courseAccessMap = buildCourseAccessMap(student.course_access || []);
      const courseMetaMap = buildCourseMetaMap(dashboardCourses, catalogCourses);

      // Show enrolled courses + public demo courses by default
      const enrolledCourses = student.enrolled_courses || [];
      const allVisibleCourses = Array.from(new Set([
        ...enrolledCourses,
        ...availableDemoCourses.map((course) => course.demo_course_code),
      ]));
      const coursesData = await Promise.all(
        allVisibleCourses.map(async (courseCode) => {
          try {
            const normalizedCourseCode = String(courseCode).toUpperCase();
            const isDemoCourse = demoCourseCodeSet.has(normalizedCourseCode);
            const isEnrolled = enrolledCourses.some(
              (code) => String(code).toUpperCase() === normalizedCourseCode,
            );
            const progressResult = await getCourseProgress(student.phone, courseCode);
            
            let daysRemaining = null;
            let isExpired = false;
            let expiryDate = null;
            if (!isDemoCourse && isEnrolled) {
              const expiryInfo = resolveCourseExpiry(student, courseCode, courseAccessMap);
              daysRemaining = expiryInfo.daysRemaining;
              isExpired = expiryInfo.isExpired;
              expiryDate = expiryInfo.expiryDate;
            }

            const meta = getCourseMeta(courseMetaMap, courseCode);
            const theme = getCourseTheme(courseCode);
            const displayName = meta.course_name || demoCourseNameMap.get(courseCode) || courseCode;

            return {
              courseCode,
              courseName: formatCourseTitle(displayName, courseCode),
              description: meta.description || '',
              thumbnailUrl: meta.thumbnail_url || null,
              enrollmentCount: meta.enrollment_count || 0,
              showEnrollmentCount: meta.show_enrollment_count !== false,
              showPracticeIde: meta.show_practice_ide === true,
              theme,
              progress: progressResult.success ? progressResult.data.progressPercentage : 0,
              lastAccessed: progressResult.data?.lastAccessedAt,
              totalContent: progressResult.data?.totalContent || 0,
              completedContent: progressResult.data?.completedContent || 0,
              isDemoCourse,
              isEnrolled,
              daysRemaining,
              isExpired,
              expiryDate,
              status: isExpired ? 'expired' : student.status
            };
          } catch (error) {
            console.error(`Failed to load progress for ${courseCode}:`, error);
            const normalizedCourseCode = String(courseCode).toUpperCase();
            const isDemoCourse = demoCourseCodeSet.has(normalizedCourseCode);
            const isEnrolled = enrolledCourses.some(
              (code) => String(code).toUpperCase() === normalizedCourseCode,
            );
            const meta = getCourseMeta(courseMetaMap, courseCode);
            const theme = getCourseTheme(courseCode);
            const displayName = meta.course_name || demoCourseNameMap.get(courseCode) || courseCode;

            return {
              courseCode,
              courseName: formatCourseTitle(displayName, courseCode),
              description: meta.description || '',
              thumbnailUrl: meta.thumbnail_url || null,
              enrollmentCount: meta.enrollment_count || 0,
              showEnrollmentCount: meta.show_enrollment_count !== false,
              showPracticeIde: meta.show_practice_ide === true,
              theme,
              progress: 0,
              lastAccessed: null,
              totalContent: 0,
              completedContent: 0,
              isDemoCourse,
              isEnrolled,
              daysRemaining: null,
              isExpired: false,
              status: student.status
            };
          }
        })
      );

      setCoursesWithProgress(coursesData);
    } catch (error) {
      console.error('Failed to load courses:', error);
      toast.error(error?.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const lastCourseRefreshAtRef = useRef(0);
  const courseRefreshIntervalRef = useRef(null);
  useEffect(() => {
    const refreshIfNeeded = () => {
      const now = Date.now();
      if (now - lastCourseRefreshAtRef.current < 5000) return;
      lastCourseRefreshAtRef.current = now;
      loadStudentAndCourses();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshIfNeeded();
    };

    window.addEventListener('focus', refreshIfNeeded);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshIfNeeded);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // If a student stays on this page while admin extends access in another tab,
  // there may be no focus/visibility events. Poll lightly while any course is expired.
  useEffect(() => {
    const hasExpired = (coursesWithProgress || []).some((c) => c?.isExpired);

    if (!hasExpired || document.visibilityState !== 'visible') {
      if (courseRefreshIntervalRef.current) {
        clearInterval(courseRefreshIntervalRef.current);
        courseRefreshIntervalRef.current = null;
      }
      return;
    }

    if (courseRefreshIntervalRef.current) return;

    courseRefreshIntervalRef.current = setInterval(() => {
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();
      if (now - lastCourseRefreshAtRef.current < 5000) return;

      lastCourseRefreshAtRef.current = now;
      loadStudentAndCourses();
    }, 15000);

    return () => {
      if (courseRefreshIntervalRef.current) {
        clearInterval(courseRefreshIntervalRef.current);
        courseRefreshIntervalRef.current = null;
      }
    };
  }, [coursesWithProgress]);

  const enrollForDemoCourse = async (courseCode) => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      toast.error('Please login first');
      navigate('/student/login');
      return false;
    }

    try {
      setEnrollingCourseCode(courseCode);
      const response = await fetch(`${API_BASE_URL}/students/courses/demo/enroll-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ course_code: courseCode, accepted_terms: true }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to enroll demo course');
      }

      const updatedEnrolledCourses = data?.student?.enrolled_courses || [];
      const nextStudentData = {
        ...(studentData || {}),
        enrolled_courses: updatedEnrolledCourses,
      };

      setStudentData(nextStudentData);
      localStorage.setItem('student_data', JSON.stringify(nextStudentData));
      setCoursesWithProgress((prev) =>
        prev.map((course) =>
          String(course.courseCode).toUpperCase() === String(courseCode).toUpperCase()
            ? { ...course, isEnrolled: true }
            : course,
        ),
      );

      toast.success('Demo course enrolled successfully');
      return true;
    } catch (error) {
      toast.error(error?.message || 'Failed to enroll demo course');
      return false;
    } finally {
      setEnrollingCourseCode(null);
    }
  };

  const persistTermsAcceptance = async () => {
    const token = localStorage.getItem('student_token');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/students/me/terms-acceptance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Could not save terms acceptance');
      }
      const sd = JSON.parse(localStorage.getItem('student_data') || '{}');
      const next = {
        ...sd,
        accepted_terms: true,
        terms_accepted_at: data?.student?.terms_accepted_at || new Date().toISOString(),
      };
      localStorage.setItem('student_data', JSON.stringify(next));
      setStudentData(next);
    } catch (e) {
      toast.error(e?.message || 'Could not save terms acceptance');
      setCourseAccessTermsOk(false);
    }
  };

  const handleCourseClick = async (course) => {
    if (course.isExpired) {
      toast.error('Course access has expired. Please contact admin');
      return;
    }

    if (!courseAccessTermsOk) {
      toast.error(
        'Please accept Terms & Conditions and confirm that fees are non-refundable before continuing.',
      );
      return;
    }

    if (course.isDemoCourse && !course.isEnrolled) {
      const enrolled = await enrollForDemoCourse(course.courseCode);
      if (!enrolled) {
        return;
      }
    }

    navigate(`/student/courses/${course.courseCode}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!studentData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">
          You are not enrolled in any courses yet. Please contact admin to enroll.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, md: 4 }, bgcolor: '#f4f6fb', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2.5, md: 4 },
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 4,
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.35)',
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={{ xs: 2, lg: 3 }}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: { xs: 56, sm: 72 },
                  height: { xs: 56, sm: 72 },
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 700,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                }}
              >
                {studentData.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  My Learning
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.92, mt: 0.5 }}>
                  Continue your learning journey, {studentData.name}
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<KeyboardIcon />}
                onClick={() => navigate('/student/typing-tutor')}
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}
              >
                Typing Tutor
              </Button>
              <Button
                component="a"
                href={STUDENT_ANDROID_APK_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.65)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.12)' } }}
              >
                Install App
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}
              >
                Logout
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2.5 }}
        >
          <Typography variant="h5" fontWeight={800}>
            My Courses ({searchQuery.trim() ? filteredCourses.length : coursesWithProgress.length})
          </Typography>
          <TextField
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              minWidth: { xs: '100%', sm: 280 },
              maxWidth: { sm: 360 },
              bgcolor: 'background.paper',
              borderRadius: 2,
            }}
          />
        </Stack>

        {searchQuery.trim() && coursesWithProgress.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredCourses.length} of {coursesWithProgress.length} course
            {coursesWithProgress.length === 1 ? '' : 's'}
          </Typography>
        )}

        <Alert severity="warning" sx={{ mb: 2, borderRadius: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
            Fees once paid are non-refundable.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={courseAccessTermsOk}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  if (!checked) {
                    setCourseAccessTermsOk(false);
                    return;
                  }
                  setCourseAccessTermsOk(true);
                  await persistTermsAcceptance();
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I agree to Terms &amp; Conditions and understand that fees are non-refundable.{' '}
                <Link component={RouterLink} to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                  Read full terms
                </Link>
              </Typography>
            }
          />
        </Alert>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          <Link component={RouterLink} to="/terms-and-conditions">
            Terms, conditions &amp; disclaimer
          </Link>
        </Typography>

        {demoCourses.length > 0 && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Demo courses are available by default for all users.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {demoCourses.map((course) => (
                <Chip
                  key={course.demo_course_code}
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={course.demo_course_name || course.demo_course_code}
                />
              ))}
            </Stack>
          </Alert>
        )}

        {coursesWithProgress.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
            You are not enrolled in any courses yet.
          </Alert>
        ) : null}

        {searchQuery.trim() && coursesWithProgress.length > 0 && filteredCourses.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
            No courses match &quot;{searchQuery.trim()}&quot;. Try a different search or clear the filter.
          </Alert>
        ) : null}

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Grid item xs={12} sm={6} lg={4} key="typing-tutor-card">
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.06)',
                boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 16px 40px rgba(79, 70, 229, 0.2)',
                },
              }}
              onClick={() => navigate('/student/typing-tutor')}
            >
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '16 / 9',
                  background:
                    'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ position: 'absolute', top: 12, left: 12, right: 12 }}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip
                    label="Practice"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.92)',
                      fontWeight: 700,
                      height: 24,
                    }}
                  />
                </Stack>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <KeyboardIcon sx={{ color: '#4F46E5', fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{
                    color: '#fff',
                    textShadow: '0 2px 8px rgba(0,0,0,0.35)',
                    lineHeight: 1.25,
                    pr: 7,
                  }}
                >
                  Typing Tutor
                </Typography>
              </Box>
              <CardContent
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: { xs: 2, sm: 2.5 },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5, minHeight: 40 }}
                >
                  Improve speed and accuracy with daily typing practice lessons.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                  <Chip
                    size="small"
                    label="Typing Practice"
                    sx={{
                      bgcolor: 'rgba(79, 70, 229, 0.12)',
                      color: '#4F46E5',
                      fontWeight: 600,
                    }}
                  />
                </Stack>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PlayIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate('/student/typing-tutor');
                  }}
                  sx={{
                    mt: 'auto',
                    borderRadius: 2.5,
                    py: 1.1,
                    fontWeight: 700,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                    boxShadow: '0 6px 18px rgba(79, 70, 229, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7C3AED, #4F46E5)',
                    },
                  }}
                >
                  Open Typing Tutor
                </Button>
              </CardContent>
            </Card>
          </Grid>
          {filteredCourses.map((course) => {
            const theme = course.theme || getCourseTheme(course.courseCode);
            const heroBackground = course.thumbnailUrl
              ? `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.72) 100%), url(${course.thumbnailUrl}) center/cover no-repeat`
              : `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`;

            return (
              <Grid item xs={12} sm={6} lg={4} key={course.courseCode}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    cursor: course.isExpired ? 'not-allowed' : 'pointer',
                    opacity: course.isExpired ? 0.75 : 1,
                    '&:hover': course.isExpired
                      ? {}
                      : {
                          transform: 'translateY(-6px)',
                          boxShadow: `0 16px 40px ${theme.soft}`,
                        },
                  }}
                  onClick={() => handleCourseClick(course)}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      aspectRatio: '16 / 9',
                      background: heroBackground,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: 2,
                    }}
                  >
                    {!course.thumbnailUrl && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -55%)',
                          fontSize: '3.5rem',
                          fontWeight: 900,
                          color: 'rgba(255,255,255,0.22)',
                          userSelect: 'none',
                        }}
                      >
                        {String(course.courseName || course.courseCode).charAt(0).toUpperCase()}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={0.75} sx={{ position: 'absolute', top: 12, left: 12, right: 12 }} useFlexGap flexWrap="wrap">
                      {course.isDemoCourse && (
                        <Chip
                          label="Demo"
                          size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.92)', fontWeight: 700, height: 24 }}
                        />
                      )}
                      {course.isExpired && (
                        <Chip icon={<LockIcon sx={{ fontSize: '14px !important' }} />} label="Expired" color="error" size="small" sx={{ height: 24 }} />
                      )}
                      {!course.isExpired && course.progress === 100 && (
                        <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label="Completed" color="success" size="small" sx={{ height: 24 }} />
                      )}
                      {course.showPracticeIde && !course.isExpired && (
                        <Chip
                          icon={<TerminalIcon sx={{ fontSize: '14px !important' }} />}
                          label="Practice IDE"
                          size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.92)', fontWeight: 700, height: 24 }}
                        />
                      )}
                    </Stack>

                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      <Typography variant="caption" fontWeight={800} sx={{ color: theme.accent }}>
                        {course.progress}%
                      </Typography>
                    </Box>

                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{
                        color: '#fff',
                        textShadow: '0 2px 8px rgba(0,0,0,0.35)',
                        lineHeight: 1.25,
                        pr: 7,
                      }}
                    >
                      {course.courseName}
                    </Typography>
                  </Box>

                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2.5 } }}>
                    {course.description ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: 40,
                        }}
                      >
                        {course.description}
                      </Typography>
                    ) : (
                      <Box sx={{ minHeight: 8, mb: 1 }} />
                    )}

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                      <Chip
                        size="small"
                        label={`${course.completedContent} / ${course.totalContent} lessons`}
                        sx={{ bgcolor: theme.soft, color: theme.accent, fontWeight: 600 }}
                      />
                      {course.showEnrollmentCount && course.enrollmentCount > 0 && (
                        <Chip size="small" variant="outlined" label={`${course.enrollmentCount} learners`} />
                      )}
                    </Stack>

                    <Box sx={{ mb: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Progress
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ color: theme.accent }}>
                          {course.progress}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={course.progress}
                        sx={{
                          height: 8,
                          borderRadius: 99,
                          bgcolor: theme.soft,
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 99,
                            background: `linear-gradient(90deg, ${theme.from}, ${theme.to})`,
                          },
                        }}
                      />
                    </Box>

                    {course.lastAccessed && (
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                        <ScheduleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          Last accessed {new Date(course.lastAccessed).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    )}

                    {!course.isExpired && typeof course.daysRemaining === 'number' && course.daysRemaining < 10 && (
                      <Alert severity="warning" sx={{ mb: 1.5, py: 0.25, borderRadius: 2 }}>
                        <Typography variant="caption">
                          {course.daysRemaining} days remaining
                        </Typography>
                      </Alert>
                    )}

                    {course.showPracticeIde && !course.isExpired && (
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<TerminalIcon />}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!courseAccessTermsOk) {
                            toast.error(
                              'Please accept Terms & Conditions and confirm that fees are non-refundable before continuing.',
                            );
                            return;
                          }
                          navigate(`/student/courses/${course.courseCode}/practice-ide`);
                        }}
                        sx={{
                          mb: 1.5,
                          borderRadius: 2.5,
                          py: 1,
                          fontWeight: 700,
                          textTransform: 'none',
                          borderColor: theme.accent,
                          color: theme.accent,
                          '&:hover': {
                            borderColor: theme.to,
                            bgcolor: theme.soft,
                          },
                        }}
                      >
                        Open Practice IDE
                      </Button>
                    )}

                    <Button
                      variant={course.isExpired ? 'outlined' : 'contained'}
                      fullWidth
                      startIcon={course.isExpired ? <LockIcon /> : <PlayIcon />}
                      disabled={course.isExpired || enrollingCourseCode === course.courseCode}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCourseClick(course);
                      }}
                      sx={{
                        mt: 'auto',
                        borderRadius: 2.5,
                        py: 1.1,
                        fontWeight: 700,
                        textTransform: 'none',
                        ...(course.isExpired
                          ? {}
                          : {
                              background: `linear-gradient(135deg, ${theme.from}, ${theme.to})`,
                              boxShadow: `0 6px 18px ${theme.soft}`,
                              '&:hover': {
                                background: `linear-gradient(135deg, ${theme.to}, ${theme.from})`,
                              },
                            }),
                      }}
                    >
                      {course.isExpired
                        ? 'Expired'
                        : enrollingCourseCode === course.courseCode
                          ? 'Enrolling...'
                          : course.isDemoCourse && !course.isEnrolled
                            ? 'Enroll for Demo'
                            : course.progress > 0
                              ? 'Continue Learning'
                              : 'Start Course'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default MyCourses;
